import { Dispatch, SetStateAction, useEffect, useMemo, useState } from "react"

import { Spinner, Table } from "react-bootstrap"
import { toast } from "react-toastify"
import { BehaviorSubject } from "rxjs"
import { filter, finalize, switchMap } from "rxjs/operators"

import DonorRow from "./DonorRow"
import { OrphanedDonor } from "../../../api/OrderRequests/OrderRequestsModel/Donor"
import DonorGridData from "../../../api/OrphanedResults/Models/DonorGridData"
import DonorGridOptions from "../../../api/OrphanedResults/Models/DonorGridOptions"
import PendingDrugTest from "../../../api/OrphanedResults/Models/PendingDrugTest"
import { GetDonorsObservable } from "../../../api/OrphanedResults/ResultExceptionsProcess"
import FormatError from "../../../formatters/FormatError"
import TablePagination from "../../TablePagination"

const headerColumns = ["First Name", "Last Name", "IDs", "Company", "Location"]
const PageSize = 5

interface DonorGridParams {
  search: { field: string; text: string }
  donorId: number
  testID: number
  setDonorID: Dispatch<SetStateAction<number>>
  setSelectedTest: Dispatch<SetStateAction<PendingDrugTest | undefined>>
}

function DonorGrid(props: DonorGridParams) {
  const { search, donorId, testID, setDonorID, setSelectedTest } = props
  const [gridData, setGridData] = useState<DonorGridData>({ Records: [], Pages: 0, Total: 0 })
  const [loading, setLoading] = useState(false)
  const page$ = useMemo(
    () =>
      new BehaviorSubject<DonorGridOptions>({
        search,
        testID,
        pageNumber: 1,
        pageSize: PageSize,
      }),
    [search, testID]
  )

  useEffect(() => {
    setLoading(true)
    const subs = page$
      .pipe(
        filter((params) => params.search.text.length > 0 || params.testID > 0),
        switchMap((params) =>
          GetDonorsObservable(params).pipe(
            finalize(() => {
              setLoading(false)
            })
          )
        )
      )
      .subscribe(
        (data) => {
          setGridData(data)
          if (data.Records.length === 1) {
            setDonorID(data.Records[0].PersonID)
          } else {
            setDonorID(0)
          }
        },
        (error) => toast.error(FormatError(error))
      )
    return () => subs.unsubscribe()
  }, [page$, search.text, setDonorID])

  const handleChangePage = (page: number) => {
    setLoading(true)
    page$.next({ ...page$.value, search, pageNumber: page })
  }

  const handleSelectDonor = (donorID: number) => {
    setSelectedTest(undefined)
    setDonorID(donorId === donorID ? 0 : donorID)
  }

  return (
    <>
      <Table className="table table-bordered">
        <thead className="thead-light">
          <tr>
            <th className="col-1" aria-label="..." />
            {headerColumns.map((el) => (
              <th key={`dh_${el}`}>{el}</th>
            ))}
          </tr>
        </thead>
        <tbody className="bg-light">
          {loading && (
            <tr>
              <td colSpan={headerColumns.length + 1}>
                <Spinner animation="border" variant="secondary" />
              </td>
            </tr>
          )}
          {!loading &&
            (gridData.Records.length > 0 ? (
              gridData.Records.map((row: OrphanedDonor) => (
                <DonorRow
                  key={`donor_${row.PersonID}`}
                  donor={row}
                  onSelectDonor={handleSelectDonor}
                  selected={donorId === row.PersonID}
                />
              ))
            ) : (
              <tr>
                <td colSpan={headerColumns.length + 1}>NO DATA FOUND</td>
              </tr>
            ))}
        </tbody>
      </Table>
      {gridData.Records.length > 0 && (
        <>
          <span className="total-records">Total records: {gridData.Total}</span>
          <TablePagination pageNumber={page$.value.pageNumber} pages={gridData.Pages} onChange={handleChangePage} />
        </>
      )}
    </>
  )
}

export default DonorGrid
